import { useEffect, useRef, useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/ui/inputs/Input';
import { Section } from '@/shared/ui/layout/Section';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/shared/ui/selects/SearchableSelect';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { isApiError } from '@/shared/api/errors';
import {
  useCargoIdLookupByCode,
  useInfiniteCargoIdLookup,
} from '@/entities/cargo-id';
import type { ApiCargoIdLookupItem } from '@/shared/types/api/cargo-id';
import type { AdminParcelCreateFormValues } from '@/features/parcel-form/modules/types';

type Props = { form: UseFormReturn<AdminParcelCreateFormValues> };

type SearchField = 'full_name' | 'phone_number';

const optionFrom = (item: ApiCargoIdLookupItem, field: SearchField): SearchableSelectOption => ({
  value: item.id,
  label: item[field],
  description: `${item.full_name} — ${item.code}`,
});

const SearchSelectField = ({
  label,
  field,
  selected,
  error,
  onPick,
}: {
  label: string;
  field: SearchField;
  selected: ApiCargoIdLookupItem | null;
  error?: string;
  onPick: (item: ApiCargoIdLookupItem) => void;
}) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(term, 350);
  // Only fetch while the dropdown is open; enabled with a blank term shows the initial page.
  const lookup = useInfiniteCargoIdLookup(debounced, open);
  const items = lookup.items;

  return (
    <SearchableSelect
      label={label}
      error={error}
      placeholder={t('parcels.form.cargoId.searchPlaceholder')}
      selectedLabel={selected ? selected[field] : ''}
      options={items.map((it) => optionFrom(it, field))}
      loading={lookup.isFetching && !lookup.isFetchingNextPage}
      hasMore={lookup.hasNextPage}
      loadingMore={lookup.isFetchingNextPage}
      onLoadMore={() => void lookup.fetchNextPage()}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      emptyText={lookup.isFetching ? '' : t('parcels.form.cargoId.noResults')}
      onSearch={setTerm}
      onSelect={(opt) => {
        const item = items.find((it) => it.id === opt.value);
        if (item) onPick(item);
      }}
    />
  );
};

export const FeaturesParcelFormSectionCargoId = ({ form }: Props) => {
  const { t } = useTranslation();
  const { control, setValue, formState } = form;
  const errors = formState.errors;

  const code = useWatch({ control, name: 'cargo_code' }) ?? '';
  const cargoId = useWatch({ control, name: 'cargo_id' }) ?? '';
  const [selected, setSelected] = useState<ApiCargoIdLookupItem | null>(null);

  // Debounced exact-code lookup. Skip when the code already matches the selection.
  const debouncedCode = useDebouncedValue(code, 350);
  const skipCodeLookup = selected != null && selected.code === debouncedCode.trim();
  const codeQuery = useCargoIdLookupByCode(skipCodeLookup ? '' : debouncedCode.trim());

  const applySelection = (item: ApiCargoIdLookupItem) => {
    setSelected(item);
    setValue('cargo_id', item.id, { shouldValidate: true, shouldDirty: true });
    setValue('cargo_code', item.code, { shouldDirty: true });
  };

  // React to code-lookup results / errors.
  const lastResolvedId = useRef<string | null>(null);
  useEffect(() => {
    if (skipCodeLookup || !debouncedCode.trim()) return;
    if (codeQuery.data && codeQuery.data.id !== lastResolvedId.current) {
      lastResolvedId.current = codeQuery.data.id;
      setSelected(codeQuery.data);
      setValue('cargo_id', codeQuery.data.id, { shouldValidate: true, shouldDirty: true });
    }
  }, [codeQuery.data, debouncedCode, skipCodeLookup, setValue]);

  useEffect(() => {
    // On a fresh failed lookup, clear the resolved id so submit is blocked.
    if (codeQuery.isError && debouncedCode.trim() && !skipCodeLookup) {
      lastResolvedId.current = null;
      setSelected(null);
      if (cargoId) setValue('cargo_id', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [codeQuery.isError, debouncedCode, skipCodeLookup, cargoId, setValue]);

  const codeNotFound =
    codeQuery.isError && isApiError(codeQuery.error)
      ? codeQuery.error.response?.status === 404
      : false;

  const codeError = codeNotFound
    ? t('parcels.form.cargoId.notFound')
    : errors.cargo_id?.message
      ? t(errors.cargo_id.message)
      : undefined;

  return (
    <Section title={t('parcels.form.sections.cargoId')}>
      <Input
        label={t('parcels.form.cargoId.code')}
        required
        placeholder={t('parcels.form.cargoId.codePlaceholder')}
        value={code}
        autoCapitalize="characters"
        inputMode="text"
        error={codeError}
        onChange={(e) => {
          const upper = e.target.value.toUpperCase();
          if (e.target.value !== upper) e.target.value = upper;
          setValue('cargo_code', upper, { shouldDirty: true });
        }}
        suffix={codeQuery.isFetching ? <span className="text-xs">…</span> : undefined}
      />
      <p className="text-xs text-ink-muted">{t('parcels.form.cargoId.searchHint')}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SearchSelectField
          label={t('parcels.form.cargoId.fullName')}
          field="full_name"
          selected={selected}
          onPick={applySelection}
        />
        <SearchSelectField
          label={t('parcels.form.cargoId.phone')}
          field="phone_number"
          selected={selected}
          onPick={applySelection}
        />
      </div>
    </Section>
  );
};
